import type { CanvasSurfaceHandle } from "@norkron/web-page-layout";

/**
 * `canvasPresentation` — THE ONE PRESENTATION POLICY every canvas document
 * surface in elaborate resolves its on-screen scale through.
 *
 * WHY IT EXISTS (the bug it closes, owner-reported 2026-07-29, verbatim: *"the
 * edit mode has bigger page although still A4 it seems which is good but there's
 * less margin/padding around it. I want the Read mode to have pages exactly at
 * the same scale, why is there any difference in how they're presented if both
 * are using canvas?!"*).
 *
 * The page GEOMETRY was never the difference — read, the paginated editor and
 * `<CanvasElaborateEditor>` all lay out through the SAME
 * `CANVAS_EDIT_PAGE_CONFIG` and the SAME `pdfz-layout.wasm`, and
 * `canvasRhythmParity.test.ts` measures that in points. The difference was a
 * SECOND transform applied to exactly one of the three mounts: `<ReadCanvas>`
 * carried a private `fitToWidth()` that measured `mount.clientWidth` after
 * mounting and, whenever the pane was narrower than the sheet, called
 * `surface.setZoom(requested * available / pageCss)`. The editors never did.
 * So the identical A4 sheet was painted at 100% while editing and at ~85% while
 * reading, and the reader's sheet floated in the slack the shrink created.
 *
 * That is the real defect in miniature: TWO MOUNTS OF ONE ENGINE, EACH DECIDING
 * ITS OWN SCALE. Deleting the stray call would have fixed today's symptom and
 * left the shape that produced it, so the fix is this module — one function, one
 * decision, and a mandatory call from every surface. A future scale policy
 * (user zoom, fit-to-width as an explicit reader control, print preview) changes
 * HERE and reaches read and edit in the same commit, or it does not ship.
 *
 * THE POLICY, TODAY: **the natural scale. Never auto-shrink.** A sheet is
 * painted at exactly the zoom its host asked for, on every surface. A pane
 * narrower than the sheet SCROLLS (`.ela-read-canvas__pages` and
 * `.pl-canvas-pages` both set `overflow-x: auto`) instead of silently rescaling
 * the document under the reader.
 *
 * WHAT THAT COSTS, stated rather than hidden. The deleted auto-fit was the cheap
 * half of WCAG 1.4.4 / 1.4.10 for a fixed-width canvas: below ~830 CSS px of
 * pane the reader now pans horizontally to read a full line. Real reflow — a
 * relayout at a narrower page config — is an engine change and was never what
 * `fitToWidth` did; it only ever scaled the raster, which shrinks the type along
 * with the page and is its own legibility failure. Neither surface claims
 * reflow. If auto-fit returns it returns HERE, for both surfaces at once, as a
 * declared reader control rather than an invisible side effect of pane width.
 */

/** Every input a scale policy may legitimately consider, measured at mount. */
export interface CanvasScaleInput {
  /** The zoom the host asked for (1 = 100%). Always honoured today. */
  readonly requestedZoom: number;
  /** The mount's usable width in CSS px (`clientWidth`); 0 when unmeasurable. */
  readonly availableWidthCss: number;
  /** The sheet's painted width in CSS px — already carries `requestedZoom`. */
  readonly pageWidthCss: number;
}

/**
 * The ABSOLUTE zoom a surface must paint at. Pure — it measures nothing itself,
 * so it is decidable in a test at any pane width without a DOM.
 *
 * It ignores `availableWidthCss` / `pageWidthCss` BY DECISION, not by oversight:
 * they are named so a policy change has its inputs already in hand, and so the
 * single call site below never has to grow a second measurement pass.
 */
export function resolveCanvasScale(input: CanvasScaleInput): number {
  const { requestedZoom } = input;
  // Guard only against a host passing a nonsensical zoom; never against a narrow
  // pane. "The pane is narrow" is a scrolling question, not a scaling one.
  if (!Number.isFinite(requestedZoom) || requestedZoom <= 0) return 1;
  return requestedZoom;
}

/** The sheet width a surface actually painted, in CSS px, or 0 when nothing is
 *  laid out yet. `.pl-page` is the sheet canvas the tile painter emits (both
 *  painters), and `style.width` is where the engine writes the zoomed width. */
export function measuredPageWidthCss(mount: HTMLElement): number {
  const page = mount.querySelector<HTMLElement>(".pl-page");
  const w = page ? Number.parseFloat(page.style.width || "0") : 0;
  return Number.isFinite(w) && w > 0 ? w : 0;
}

/**
 * The BLOCK-axis margin every canvas DOCUMENT surface gives its page chain.
 *
 * Owner 2026-07-31, verbatim: *"`.pl-canvas-doc` — `margin: 12px auto`"*. The
 * `auto` half is the engine's default centring (`align: 'center'`); this constant
 * is the `12px` half — the breathing room that stops the first sheet butting
 * against the top of its scroller.
 *
 * ⚠️ IT IS PASSED, NOT STYLED, AND THAT IS NOT A PREFERENCE. `@norkron/web-page-
 * layout`'s tile painter writes `.pl-canvas-doc`'s `margin` as an INLINE style and
 * RE-WRITES it on every re-plan (zoom, dpr change, relayout). A stylesheet rule in
 * this app therefore loses the cascade to it and could only win with
 * `!important` — an escape hatch this exact property has already been rescued from
 * once (web-page-layout added `align` specifically to delete
 * `quoteCanvas.css`'s `.pl-canvas-doc { margin: 0 !important }`). Re-introducing it
 * for the block axis would re-open that hole, so the block axis became an option
 * too: `blockMarginCss` (web-page-layout, 2026-07-31).
 *
 * ⛔ FRAGMENT surfaces must NOT use this. The comment and quote canvases
 * (`CommentCanvasBody`, `QuoteCanvas`) are clippings set flush in a card row, not
 * documents floating in a pane — `quoteCanvas.css` documents at length why a
 * fragment is pinned flush. This constant is for the two DOCUMENT surfaces only:
 * the reader (`<ReadCanvas>`, which the past-revision view composes) and the
 * editor (`<PaginatedCanvasEditor>`).
 */
export const DOCUMENT_BLOCK_MARGIN_CSS = "12px";

/**
 * Apply {@link resolveCanvasScale} to a freshly mounted surface.
 *
 * EVERY canvas document surface calls this immediately after `createCanvasSurface`
 * / `createCanvasEditor` returns. Under today's policy the call is a no-op on all
 * of them — and that is exactly the point: the no-op is the seam that makes the
 * policy single. A surface that does not call it is a surface that can drift, and
 * `canvasScaleParity.test.tsx` fails when one does.
 *
 * Returns the scale in force, so a caller can assert on it.
 */
export function applyCanvasScale(
  mount: HTMLElement,
  surface: Pick<CanvasSurfaceHandle, "setZoom">,
  requestedZoom: number,
): number {
  const scale = resolveCanvasScale({
    requestedZoom,
    availableWidthCss: mount.clientWidth,
    pageWidthCss: measuredPageWidthCss(mount),
  });
  // `setZoom` is idempotent upstream (it returns early on an unchanged value),
  // but calling it unconditionally would still re-plan tiles on every remount
  // under a policy that returns a computed number, so compare here.
  if (scale !== requestedZoom) surface.setZoom(scale);
  return scale;
}
